import {Table, Grid, Tabs, Tab, Paper, TableRow, TableHead, TableCell, TableContainer, TableBody, Collapse} from '@mui/material';
import {Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement} from 'chart.js'; 
import {useTranslation} from "react-i18next";
import {Line, Bar} from "react-chartjs-2";
import {observer} from 'mobx-react';
import {useState} from 'react';
import {TAB_STATS, STAB_GEN, STAB_NUM, STAB_DATE, STAB_MONTHS, STAB_YEARS} from './TaxCommon.js';
import {monthNames, getCityName, getCountryName} from '../Trans.js';
import {useGlobalStorage} from '../mobx/GlobalStorage.js';
import {TT_SUBSPEC, TT_SPEC, dStr} from '../Util.js';
import StatsFilter from '../stats/StatsFilter.js';
import {COMM_OK} from '../WsR2.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement);

const TaxGenStats = observer(() => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	
	const tax = gStorage.taxons.centerTaxon;

	function taxGenStatsRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('taxGenStatsRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'tax' && msg.op === 'gen_stats') {
			if (msg.result === 'OK') {
// 				console.log('taxGenStatsRxWs', msg.data);
				gStorage.taxons.setGenStats(msg.data);
			} else {
				console.error('taxGenStatsRxWs', msg.result);
			}
		} else {
			console.error('taxGenStatsRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function loadGenStats(tax) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'tax',
			op: 'gen_stats',
			data: {
				taxon: {
					id: tax.id,
				},
				user: {
					id: /*gStorage.taxons.stat_params.all_users ? 0 : */gStorage.conn.user.id,
				},
				year: gStorage.taxons.stat_params.year,
				country: gStorage.taxons.stat_params.country.id,
			}
		};

// 		console.log('loadGenStats', msg);
		gStorage.conn.ws.send(msg, taxGenStatsRxWs);
	}

	if (gStorage.taxons.stat_tab !== STAB_GEN)
		return null;

// 	console.log('TaxGenStats');

	if (gStorage.conn.online && (tax.t_type === TT_SPEC || tax.t_type === TT_SUBSPEC)) {
		if (!gStorage.taxons.gen_stats) {
			loadGenStats(tax);
			return null;
		}
	}

	
	if (!gStorage.taxons.gen_stats || !gStorage.taxons.gen_stats.observations)
		return null;

// 	console.log('genStats', gStorage.taxons.gen_stats.observations);

	return (		
		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
			<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
				<Table size='small'>
					<TableBody>
						{gStorage.taxons.stat_params.year === 0 && 
							<TableRow key={1}>
								<TableCell padding='none'>
									{t('taxons.first')}
								</TableCell>
								<TableCell align='right'>
									{gStorage.taxons.gen_stats.observations.first ? dStr(gStorage.taxons.gen_stats.observations.first) : '--'}
								</TableCell>
							</TableRow>
						}
						{gStorage.taxons.stat_params.year === 0 &&
							<TableRow key={2}>
								<TableCell padding='none'>
									{t('taxons.last')}
								</TableCell>
								<TableCell align='right'>
									{gStorage.taxons.gen_stats.observations.last ? dStr(gStorage.taxons.gen_stats.observations.last) : '--'}
								</TableCell>
							</TableRow>
						}

						<TableRow key={3}>
							<TableCell padding='none'>
								{t('taxons.earliest')}
							</TableCell>
							<TableCell align='right'>
								{gStorage.taxons.gen_stats.observations.earliest ? dStr(gStorage.taxons.gen_stats.observations.earliest) : '--'}
							</TableCell>
						</TableRow> 

						<TableRow key={4}>
							<TableCell padding='none'>
								{t('taxons.latest')}
							</TableCell>
							<TableCell align='right'>
								{gStorage.taxons.gen_stats.observations.latest ? dStr(gStorage.taxons.gen_stats.observations.latest) : '--'}
							</TableCell>
						</TableRow> 
					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
	);
});

const TaxNumStats = observer(() => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	let tax = gStorage.taxons.centerTaxon;

	function taxNumStatsRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('taxNumStatsRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'tax' && msg.op === 'stats') {
			if (msg.result === 'OK') {
// 				console.log('taxNumStatsRxWs', msg.data);
				gStorage.taxons.setStats(msg.data);
			} else {
				console.error('taxNumStatsRxWs', msg.result);
			}
		} else {
			console.error('taxNumStatsRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function loadStats(tax) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'tax',
			op: 'stats',
			data: {
				taxon: {
					id: tax.id,
				},
				user: {
					id: /*gStorage.taxons.stat_params.all_users ? 0 : */gStorage.conn.user.id,
				},
				year: gStorage.taxons.stat_params.year,
				country: gStorage.taxons.stat_params.country.id,
			}
		};

// 		console.log('loadStats', msg);
		gStorage.conn.ws.send(msg, taxNumStatsRxWs);
	}

	function taxStatGetCityName(id) {
		const usr = gStorage.conn.user;
		const city = gStorage.lists.citylist.list.find(city => city.id === id);
		let a_str = '';

		if (city.country !== usr.def_country) {
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);
			a_str = getCountryName(country, usr) + ', ';
		}

		return a_str += getCityName(city, usr);
	}

	function UserCityTable() {
		let tot_cnt, tot_num;

		if (!gStorage.taxons.stats || !gStorage.taxons.stats.observations.cities.length)
			return null;

		tot_cnt = 0;
		tot_num = 0;

		return (<div>	
				<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
					<Table size='small'>
						<TableHead>
							<TableRow>
								<TableCell padding='none'>#</TableCell>
								<TableCell>{t('add.city')}</TableCell>
								<TableCell align='right'>{t('taxons.count2')}</TableCell>
								<TableCell align='right'>{t('taxons.num2')}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{gStorage.taxons.stats.observations.cities.map((ci, ind) => {
								tot_cnt += ci.cnt;
								tot_num += ci.num;

								return (<TableRow key={ci.id}>
										<TableCell padding='none'>{ind + 1}</TableCell>
										<TableCell>{taxStatGetCityName(ci.id)}</TableCell>
										<TableCell align='right'>{ci.cnt}</TableCell>
										<TableCell align='right'>{ci.num}</TableCell>
									</TableRow>);
							})}
							<TableRow key={100000} style={{fontWeight: 'bold'}}>
								<TableCell padding='none'></TableCell>
								<TableCell></TableCell>
								<TableCell align='right'>{tot_cnt}</TableCell>
								<TableCell align='right'>{tot_num}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</div>);

	}

	if (gStorage.taxons.stat_tab !== STAB_NUM)
		return null;

	if (gStorage.conn.online && (tax.t_type === TT_SPEC || tax.t_type === TT_SUBSPEC)) {
		if (!gStorage.taxons.stats) {
			loadStats(tax);
			return null;
		}
	}

	return (
		<Grid size={{xs: 12}} align={'center'}>
			<UserCityTable/>
		</Grid>
	);
});

/* -------------------------------------------------------------------------- */

const TaxDateRow = (props) => {
	const [isOpen, setOpen] = useState(false);
	const {t} = useTranslation('obs');
	const start = props.month * 31;
	const end = start + 31;
	
	if (!props.data || !props.data.observations || !props.data.observations.dates)
		return null;

	const m_slice = props.data.observations.dates.slice(start, end);
	
	// console.log('month', props.month, 'slice', m_slice);

	let m_cnt = 0;

	m_slice.forEach(m => {
		if (m > 0)
			m_cnt++;
	});

	const tglOpen = () => {
		setOpen(!isOpen);
	}

	const month = (props.month + 1);

	let c = 'default';
	if (m_cnt < 1)
		c = 'darksalmon';

	return (
		<>
			<TableRow key={props.month} style={{'& > *': { borderBottom: 'unset' }}} onClick={tglOpen} sx={{cursor: 'pointer'}}>
				<TableCell size='small' sx={{color: c}}>
					{t(monthNames[props.month]) + ' (' + m_cnt + ')'}
				</TableCell>
				<TableCell size='small' align='right'>
					&nbsp;
				</TableCell>
			</TableRow>
			<TableRow key={12 + props.month}>
				<TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
					<Collapse in={isOpen} timeout="auto" unmountOnExit>
					{
						m_slice.map((num, day) => {
							if ((month === 2 && day > 28) || (((month === 4) || (month === 6) || (month === 9) || (month === 11)) && day > 29))
								return null;

							let c = 'default';

							if (num < 1)
								c = 'darksalmon';

							return (
								<Table key={24 + (props.month * 31) + day}>
									<TableBody>
										<TableRow>
											<TableCell sx={{ color: c, paddingBottom: 0, paddingTop: 0 }}>
												{day + 1 + '.' + month + '.'}
											</TableCell>
											<TableCell align='right' sx={{ paddingBottom: 0, paddingTop: 0 }}>
												{num ? num : ''}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							)
						})
					}
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	)
}
	
const TaxDateStats = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	const tax = gStorage.taxons.centerTaxon;

	function taxDateStatsRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('taxDateStatsRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'tax' && msg.op === 'date_stats') {
			if (msg.result === 'OK') {
// 				console.log('taxDateStatsRxWs', msg.data);
				gStorage.taxons.setDateStats(msg.data);
			} else {
				console.error('taxDateStatsRxWs', msg.result);
			}
		} else {
			console.error('taxDateStatsRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}

	function loadDateStats(tax) {
		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'tax',
			op: 'date_stats',
			data: {
				taxon: {
					id: tax.id,
				},
				user: {
					id: /*gStorage.taxons.stat_params.all_users ? 0 : */gStorage.conn.user.id,
				},
				year: gStorage.taxons.stat_params.year,
				country: gStorage.taxons.stat_params.country.id,
			}
		};

// 		console.log('loadDateStats', msg);
		gStorage.conn.ws.send(msg, taxDateStatsRxWs);
	}

	if (gStorage.taxons.stat_tab !== STAB_DATE)
		return null;

	if (gStorage.conn.online && (tax.t_type === TT_SPEC || tax.t_type === TT_SUBSPEC)) {
		if (!gStorage.taxons.date_stats) {
			loadDateStats(tax);
			return null;
		}
	}

	let tot_cnt = 0;

	if (gStorage.taxons.date_stats) {
		gStorage.taxons.date_stats.observations.dates.forEach((num) => {
			if (num > 0)
				tot_cnt++;
		})
	}

	return (
		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
			<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell size='small'>{t('stats.month')}</TableCell>
							<TableCell size='small' align='right'>{t('common.sum_short') + ' ' + tot_cnt}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TaxDateRow month={0} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={1} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={2} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={3} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={4} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={5} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={6} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={7} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={8} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={9} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={10} data={gStorage.taxons.date_stats}/>
						<TaxDateRow month={11} data={gStorage.taxons.date_stats}/>
					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
	)
});

/* -------------------------------------------------------------------------- */

const QuarterElem = (props) => {
	const {elem} = props;

	if (!elem.cnt)
		return null;

	return (
		<div>
			<div>
				{elem.cnt ? elem.cnt : <div>&nbsp;</div>}
			</div>
			<div>
				{elem.cnt ? elem.num : <div>&nbsp;</div>}
			</div>

		</div>
	);
};

const TaxMonStats = observer(() => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	let sum_item = [{cnt:0,num:0},{cnt:0,num:0},{cnt:0,num:0},{cnt:0,num:0}];
	let tot_sum_item = {cnt:0,num:0};
	let birds = [], obs = [], labels = [];

	function taxMonthStatsRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('taxRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'tax' && msg.op === 'month_stats') {
			if (msg.result === 'OK') {
// 				console.log('taxMonthStatsRxWs', msg.data);
				gStorage.taxons.setMonthStats(msg.data);
			} else {
				console.error('taxMonthStatsRxWs', msg.result);
			}
		} else {
			console.error('taxMonthStatsRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}
	
	function loadMonthStats(tax) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'tax',
			op: 'month_stats',
			data: {
				taxon: {
					id: tax.id,
				},
				user: {
					id: /*gStorage.taxons.stat_params.all_users ? 0 :*/ gStorage.conn.user.id,
				},
				year: gStorage.taxons.stat_params.year,
				country: gStorage.taxons.stat_params.country.id,
			}
		};

// 		console.log('loadMonthStats', msg);
		gStorage.conn.ws.send(msg, taxMonthStatsRxWs);
	}

	if (gStorage.taxons.stat_tab !== STAB_MONTHS)
		return null;

	const tax = gStorage.taxons.centerTaxon;

	if (gStorage.conn.online && (tax.t_type === TT_SPEC || tax.t_type === TT_SUBSPEC)) {
		if (!gStorage.taxons.month_stats) {
			loadMonthStats(tax);
			return null;
		}
	} 

	if (!gStorage.taxons.month_stats || !gStorage.taxons.month_stats.observations)
		return null;

	gStorage.taxons.month_stats.observations.forEach((item, pos) => {
// 		labels.push('', t(monthNames[pos]), '', '');
		item.forEach((i, ind) => {
			if (ind % 4 === 1) {
				labels.push(pos + 1);
			} else {
				labels.push('');
			}

			obs.push(i.cnt);
			birds.push(i.num);
		});
	});

	return (
		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
			<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell padding='none'>{t('stats.month')}</TableCell>
							<TableCell align='center'><span aria-label="moon 1/4" role="img">&#127762;</span></TableCell>
							<TableCell align='center'><span aria-label="moon 1/2" role="img">&#127763;</span></TableCell>
							<TableCell align='center'><span aria-label="moon 3/4" role="img">&#127764;</span></TableCell>
							<TableCell align='center'><span aria-label="moon full" role="img">&#127765;</span></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{gStorage.taxons.month_stats.observations.map((item, ind) => {
							let fontColor = '', i;

							if (item[0].cnt + item[1].cnt + item[2].cnt + item[3].cnt <= 0) 
								fontColor = 'darksalmon';

							for (i = 0; i < 4; i++) {
								sum_item[i].cnt += item[i].cnt;
								sum_item[i].num += item[i].num;

								tot_sum_item.cnt += item[i].cnt;
								tot_sum_item.num += item[i].num;
							}

							return (
								<TableRow key={ind}>
									<TableCell padding='none' style={{color: fontColor}}>
										{t(monthNames[ind])}
									</TableCell>
									<TableCell align='center'>
										<QuarterElem elem={item[0]}/>
									</TableCell>
									<TableCell align='center'>
										<QuarterElem elem={item[1]}/>
									</TableCell>
									<TableCell align='center'>
										<QuarterElem elem={item[2]}/>
									</TableCell>
									<TableCell align='center'>
										<QuarterElem elem={item[3]}/>
									</TableCell>
								</TableRow>
							);
						})}
						<TableRow key={12}>
							<TableCell padding='none' style={{fontWeight: 'bold'}}>
								{t('common.sum')}
							</TableCell>
							<TableCell align='center' style={{fontWeight: 'bold'}}>
								<QuarterElem elem={sum_item[0]}/>
							</TableCell>
							<TableCell align='center' style={{fontWeight: 'bold'}}>
								<QuarterElem elem={sum_item[1]}/>
							</TableCell>
							<TableCell align='center' style={{fontWeight: 'bold'}}>
								<QuarterElem elem={sum_item[2]}/>
							</TableCell>
							<TableCell align='center' style={{fontWeight: 'bold'}}>
								<QuarterElem elem={sum_item[3]}/>
							</TableCell>
						</TableRow>
						<TableRow key={13}>
							<TableCell padding='none' style={{fontWeight: 'bold'}}>
								{t('common.sum')}
							</TableCell>
							<TableCell style={{fontWeight: 'bold'}} align='right' colSpan={4}>
								<QuarterElem elem={tot_sum_item}/>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>

			<Grid size={{xs: 12}} align={'center'}>
				<div style={{width: 300}}>
					&nbsp;
				</div>
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<div style={{width: 300}}>
					<Line 
						data={{
							labels: labels,
							datasets: [{
								label: t('taxons.num'),
								data: birds,
								borderColor: 'red',
								borderWidth: 1,
								pointRadius: 2,
								cubicInterpolationMode: 'monotone',
							}, {
								label: t('taxons.count'),
								data: obs,
								borderColor: 'green',
								borderWidth: 1,
								pointRadius: 2,
								cubicInterpolationMode: 'monotone',
							}]
						}}

						options = {{
							maintainAspectRatio: true,
							title: {
								display: false,
							},
							plugins: {
								legend: { 
									display: false, 
								},
							},
							scales: {
								xAxes: {
									display: true,
									ticks: {
										autoSkip: false,
										maxRotation: 0,
										minRotation: 0
									}
								},
								y: {
									min: 0,
								}
							},
							ticks: {
								precision: 0
							}
						}}
					/>
				</div>
			</Grid>
			<Grid size={{xs: 12}} align={'center'}>
				<div style={{width: 300}}>
					&nbsp;
				</div>
			</Grid>
	       </Grid>
	);
});

const TaxYearsStats = observer(() => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	let tot;

	if (gStorage.taxons.stat_tab !== STAB_YEARS)
		return null;

	function taxYearsStatsRxWs(status, msg) {
		if (status !== COMM_OK) {
			console.error('taxYearsStatsRxWs: error ', status);
			return -1;
		}

		if (msg.cmd === 'tax' && msg.op === 'years_stats') {
			if (msg.result === 'OK') {
// 				console.log('taxYearsStatsRxWs', msg.data);
				gStorage.taxons.setYearsStats(msg.data);
			} else {
				console.error('taxYearsStatsRxWs', msg.result);
			}
		} else {
			console.error('taxYearsStatsRxWs: unknown command', msg.cmd, msg.op, msg.result);
		}
	}
	
	function loadYearsStats(tax) {
		let msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'tax',
			op: 'years_stats',
			data: {
				taxon: {
					id: tax.id,
				},
				user: {
					id: gStorage.conn.user.id,
				},
				year: gStorage.taxons.stat_params.year,
				country: gStorage.taxons.stat_params.country.id,
			}
		};

// 		console.log('loadYearsStats', msg);
		gStorage.conn.ws.send(msg, taxYearsStatsRxWs);
	}

	const tax = gStorage.taxons.centerTaxon;

	if (gStorage.conn.online && (tax.t_type === TT_SPEC || tax.t_type === TT_SUBSPEC)) {
		if (!gStorage.taxons.year_stats) {
			loadYearsStats(tax);
			return null;
		}
	} 

	if (!gStorage.taxons.year_stats || !gStorage.taxons.year_stats.observations || !gStorage.taxons.year_stats.observations.length)
		return null;

	let dataset = {
		label: '',
		data: [], 
		borderColor: 'green',
		borderWidth: 1,
		pointRadius: 2,
	};

	let max_y = 0;

	gStorage.taxons.year_stats.observations.forEach((e) => {
		dataset.data.push(e.cnt);

		if (e.cnt > max_y)
			max_y = e.cnt;
	});

	tot = 0;

	return (
		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
			<Grid size={{xs: 12}} sx={{mb: 2}} align={'center'}>
				<div style={{width: 300}}>
					<Bar
						data={{
							labels: new Array(dataset.data.length).fill(''),
							datasets: [dataset],
						}}

						options = {{
							responsive: true,
							plugins: {
								legend: {
									position: 'top',
									display: false,
								},
								title: {
									display: false,
								}
							},
							scales: {
								y: {
									min: 0,
									max: max_y,
									ticks: {
										beginAtZero: true,
										callback: (label, index, labels) => {
											if (Math.floor(label) === label) {
												return label;
											}
										}
									}
								},
								x: {
									ticks: {
										callback: function(val, index) {
											return gStorage.taxons.year_stats.observations[index].year;
										},
										autoSkip: false,
										minRotation: 0,
										maxRotation: 90,
									}, 
								}
							},
						}}
					/>
				</div>
			</Grid>

			<TableContainer component={Paper} elevation={0} style={{width: 300, margin: 2}}>
				<Table size='small'>
					<TableHead>
						<TableRow>
							<TableCell padding='none'>#</TableCell>
							<TableCell align='left'>{t('stats.year')}</TableCell>
							<TableCell align='right'>{t('stats.observations')}</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{gStorage.taxons.year_stats.observations.map((item, ind) => {
							tot += item.cnt;

							return (
								<TableRow key={ind}>
									<TableCell padding='none'>
										{ind + 1}
									</TableCell>
									<TableCell align='left'>
										{item.year}
									</TableCell>
									<TableCell align='right'>
										{item.cnt}
									</TableCell>
								</TableRow>
							)
						})}
						<TableRow key={-1}>
							<TableCell padding='none' style={{fontWeight: 'bold'}} colspan={2}>
								{t('common.sum')}
							</TableCell>
							<TableCell align='right' style={{fontWeight: 'bold'}}>
								{tot}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Grid>
	)
});

const TaxStats = observer(() => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	function handleTabChange(event, tab) {
		gStorage.taxons.setStatsTab(tab);
	}

	function clearAllTaxonStats() {
		gStorage.taxons.setGenStats(null);
		gStorage.taxons.setStats(null);
		gStorage.taxons.setMonthStats(null);
		gStorage.taxons.setYearsStats(null);
		gStorage.taxons.setDateStats(null);
	}

	function taxStatsCountryChange(e) {
		const country = gStorage.lists.countrylist.find(c => c.id === e.target.value);

		gStorage.taxons.stat_params.setCountry(country);

		clearAllTaxonStats();
	}

	function taxStatsYearChange(e) {
		console.log('taxStatsYearChange', e.target.value);

		gStorage.taxons.stat_params.setYear(parseInt(e.target.value, 10));

		clearAllTaxonStats();
	}

	if (gStorage.taxons.tab !== TAB_STATS)
		return null;
	
	if (!gStorage.taxons.centerTaxon) 
		return null;

	return (
		<Grid size={{xs: 12}} align={'center'}>
			<Tabs style={{width: 300}} 
				value={gStorage.taxons.stat_tab} 
				onChange={handleTabChange} 
				align={'center'}
				indicatorColor="primary"
				textColor="primary"
				variant="scrollable"
				scrollButtons="auto">
				<Tab 
					label={t('taxons.generic')}
					disabled={!gStorage.conn.online || !gStorage.taxons.centerTaxon || gStorage.taxons.centerTaxon.t_type !== TT_SPEC || gStorage.taxons.centerTaxon.t_type === TT_SUBSPEC}
				/>
				<Tab 
					label={t('taxons.obs_num')}
					disabled={!gStorage.conn.online || !gStorage.taxons.centerTaxon || gStorage.taxons.centerTaxon.t_type !== TT_SPEC || gStorage.taxons.centerTaxon.t_type === TT_SUBSPEC}
				/>
				<Tab 
					label={t('taxons.obs_daily')}
					disabled={!gStorage.conn.online || !gStorage.taxons.centerTaxon || gStorage.taxons.centerTaxon.t_type !== TT_SPEC || gStorage.taxons.centerTaxon.t_type === TT_SUBSPEC}
				/>
				<Tab 
					label={t('taxons.obs_monthly')}
					disabled={!gStorage.conn.online || !gStorage.taxons.centerTaxon || gStorage.taxons.centerTaxon.t_type !== TT_SPEC || gStorage.taxons.centerTaxon.t_type === TT_SUBSPEC}
				/>
				<Tab 
					label={t('taxons.obs_yearly')}
					disabled={!gStorage.conn.online || !gStorage.taxons.centerTaxon || gStorage.taxons.centerTaxon.t_type !== TT_SPEC || gStorage.taxons.centerTaxon.t_type === TT_SUBSPEC}
				/>
			</Tabs>

			<Grid size={{xs: 12}} align={'center'}>
				<StatsFilter 
					loc = {{country: {
							value: gStorage.taxons.stat_params.country,
							style: {width: 200, marginTop: 16},
							onChange: taxStatsCountryChange,
						}
					}}
					time = {{
						year: {
							year: gStorage.taxons.stat_params.year,
							onChange: taxStatsYearChange,
							enabled: gStorage.conn.online,
							allowNone: true
						}
					}}
				/>
			</Grid>

			<TaxGenStats/>
			<TaxNumStats/>
			<TaxDateStats/>
			<TaxMonStats/>
			<TaxYearsStats/>
		</Grid>
	);
});

export default TaxStats;
